import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { FetchHelper } from "../utils/fetchHelper"
import { flattenMenu } from "../utils"
import { config } from "../Data/constant"

// -------------------------------------------------------------
// CONTEXT SETUP
// -------------------------------------------------------------
// Centralized permission context used across the application.
//
// Provides:
// - current permission
// - canManage flag
// - unauthorized state
// - menu items
// - navigation helpers
//
const RoutePermissionContext = createContext(null)

export const RoutePermissionProvider = ({ children }) => {

    // -------------------------------------------------------------
    // RUNTIME CONFIG STATES
    // -------------------------------------------------------------
    // These values are injected from host application runtime config.
    //
    // allowedNonMenuRoutes:
    // Routes that bypass permission checks completely.
    //
    // defaultPermissionConfig:
    // Permission-level configuration mapping.
    //
    const [allowedNonMenuRoutes, setAllowedNonMenuRoutes] = useState([])
    const [defaultPermissionConfig, setDefaultPermissionConfig] = useState({})

    // -------------------------------------------------------------
    // GET STORED MENU
    // -------------------------------------------------------------
    // Retrieves cached menu data from localStorage.
    //
    // Why needed:
    // Avoids permission flicker and unnecessary API dependency
    // during initial app load or refresh.
    //
    const getStoredMenu = () => {
        const menuStorageKey = `${config?.STORAGE_KEY_PREFIX}_${sessionStorage.getItem("app_key")}`

        try {
            const data = JSON.parse(localStorage.getItem(menuStorageKey))
            return Array.isArray(data) ? data : []
        } catch {
            return []
        }
    }

    const { pathname, search } = useLocation()
    const navigate = useNavigate()

    const [currentRole, setCurrentRole] = useState(null)

    // -------------------------------------------------------------
    // MENU STATE
    // -------------------------------------------------------------
    // Initialized from localStorage cache.
    //
    const [menuItems, setMenuItemsState] = useState(getStoredMenu)

    // -------------------------------------------------------------
    // MENU FETCH TRACKER
    // -------------------------------------------------------------
    // Distinguishes:
    //
    // 1. Menu not loaded yet
    // 2. Menu loaded but empty (no permissions)
    //
    const [menuFetched, setMenuFetched] = useState(false)

    // -------------------------------------------------------------
    // WATCH APP SWITCHES
    // -------------------------------------------------------------
    // Re-loads menu cache when pathname changes.
    //
    // Useful when application/tenant changes dynamically.
    //
    useEffect(() => {
        setMenuItemsState(getStoredMenu())
    }, [pathname])

    // -------------------------------------------------------------
    // LOAD GLOBAL PERMISSION CONFIG
    // -------------------------------------------------------------
    // Reads runtime permission config injected into window object.
    //
    // Example:
    // window.globalPermission = {
    //   ALLOWED_NON_MENU_ROUTES: [],
    //   DEFAULT_PERMISSION_CONFIG: {}
    // }
    //
    useEffect(() => {
        if (window?.globalPermission) {
            setAllowedNonMenuRoutes(
                window.globalPermission.ALLOWED_NON_MENU_ROUTES || []
            )

            setDefaultPermissionConfig(
                window.globalPermission.DEFAULT_PERMISSION_CONFIG || {}
            )
        }
    }, [window?.globalPermission])

    // -------------------------------------------------------------
    // FETCH MENU CONFIGURATION
    // -------------------------------------------------------------
    // Fetches latest permission menu from backend API.
    //
    // Responsibilities:
    // - updates local state
    // - caches menu in localStorage
    // - stores current role
    //
    // Skips fetch if:
    // - token missing
    // - app_key missing
    //
    const refreshMenuItems = useCallback(async () => {

        if (!localStorage.getItem("token")) return
        if (!sessionStorage.getItem("app_key")) return

        try {

            const res = await FetchHelper.get(
                "api/v1/config-data",
                { type: "app_module" }
            )

            const menu = res?.data?.data?.config_data?.[0]?.data
            const role = res?.data?.data?.role

            const menuStorageKey =
                `${config?.STORAGE_KEY_PREFIX}_${sessionStorage.getItem("app_key")}`

            // Store menu in state + cache
            if (Array.isArray(menu)) {
                setMenuItemsState(menu)
                localStorage.setItem(menuStorageKey, JSON.stringify(menu))
            }

            setCurrentRole(role)

        } catch (err) {

            console.warn("Menu fetch failed", err)

        } finally {

            setMenuFetched(true)
        }

    }, [])

    // Initial menu fetch on app load
    useEffect(() => {
        refreshMenuItems()
    }, [])

    // -------------------------------------------------------------
    // NORMALIZE ROUTE
    // -------------------------------------------------------------
    // Purpose:
    // Creates a standardized route format for permission matching.
    //
    // Why needed:
    // Routes may contain dynamic query params like:
    //
    // /assets-detail?slug=patients&id=123
    //
    // We only want:
    //
    // /assets-detail?slug=patients
    //
    // Extra params should not affect permission checks.
    //
    const normalizeRoute = (route = "") => {

        const [path, query = ""] = route.split("?")

        const params = new URLSearchParams(query)

        // Preserve slug because it uniquely identifies asset pages
        const slug = params.get("slug")

        return slug
            ? `${path}?slug=${slug}`
            : path
    }

    // -------------------------------------------------------------
    // INDEX MENU ROUTES
    // -------------------------------------------------------------
    // Converts nested menu structure into a fast lookup map.
    //
    // Why needed:
    // Prevents recursive tree searching on every route change.
    //
    // Indexed:
    // - main links
    // - sub_links
    //
    const indexedMenu = useMemo(() => {

        // Flatten nested menu tree
        const flat = flattenMenu(menuItems)

        // Structure:
        // key   -> normalized route
        // value -> menu config
        const routeMap = new Map()

        flat.forEach((item) => {

            // -------------------------------------------------
            // INDEX MAIN LINK
            // -------------------------------------------------
            if (item?.link) {
                routeMap.set(
                    normalizeRoute(item.link),
                    item
                )
            }

            // -------------------------------------------------
            // INDEX SUB LINKS
            // -------------------------------------------------
            // Allows dynamic detail/add-update route permissions
            // without hardcoded logic.
            //
            if (Array.isArray(item?.sub_links)) {

                item.sub_links.forEach((subLink) => {

                    routeMap.set(
                        normalizeRoute(subLink),
                        item
                    )

                })
            }
        })

        return routeMap

    }, [menuItems])

    // -------------------------------------------------------------
    // ROUTE PERMISSION RESOLVER
    // -------------------------------------------------------------
    // Determines:
    //
    // 1. currentPermission
    // 2. isUnauthorized
    // 3. shouldGrantFullAccess
    //
    // Re-runs whenever:
    // - route changes
    // - menu changes
    // - query params change
    //
    const {
        currentPermission,
        isUnauthorized,
        shouldGrantFullAccess,
    } = useMemo(() => {

        // ---------------------------------------------------------
        // CHECK SESSION
        // ---------------------------------------------------------
        const hasSession = !!localStorage.getItem("token")

        // ---------------------------------------------------------
        // REMOVE PUBLIC URL PREFIX
        // ---------------------------------------------------------
        // Example:
        // PUBLIC_URL = /fleet
        //
        // Browser route:
        // /fleet/dashboard
        //
        // Normalized:
        // /dashboard
        //
        const basePath = process.env.PUBLIC_URL || ""

        const normalizedPathname =
            basePath && pathname.startsWith(basePath)
                ? pathname.slice(basePath.length) || "/"
                : pathname

        // Full current route with query params
        const fullRoute = `${normalizedPathname}${search}`

        // ---------------------------------------------------------
        // CHECK WHITELISTED ROUTES
        // ---------------------------------------------------------
        // These routes bypass permission checks.
        //
        // Examples:
        // /
        // /login
        //
        const isWhitelisted = allowedNonMenuRoutes?.some(
            (route) =>
                route === normalizedPathname ||
                route === normalizedPathname.replace(/^\//, "")
        )

        // ---------------------------------------------------------
        // ALLOW PUBLIC ROUTES
        // ---------------------------------------------------------
        if (!hasSession || isWhitelisted) {

            return {
                currentPermission: null,
                isUnauthorized: false,
                shouldGrantFullAccess: false,
            }
        }

        // ---------------------------------------------------------
        // NORMALIZE CURRENT ROUTE
        // ---------------------------------------------------------
        // Example:
        //
        // /assets-detail/details-page?slug=patients&id=123
        //
        // becomes:
        //
        // /assets-detail/details-page?slug=patients
        //
        const normalizedCurrentRoute =
            normalizeRoute(fullRoute)

        // ---------------------------------------------------------
        // FIND MATCHING MENU CONFIG
        // ---------------------------------------------------------
        // Checks:
        // - main links
        // - sub_links
        //
        const match =
            indexedMenu.get(normalizedCurrentRoute)

        // ---------------------------------------------------------
        // CHECK IF ROUTE HAS PERMISSION KEY
        // ---------------------------------------------------------
        const hasPermissionKey =
            typeof match?.permission === "string" &&
            match.permission.trim().length > 0

        return {

            // Example:
            // manage
            // view
            currentPermission:
                hasPermissionKey
                    ? match.permission
                    : null,

            // -----------------------------------------------------
            // UNAUTHORIZED CONDITIONS
            // -----------------------------------------------------
            // Unauthorized when:
            //
            // 1. Menu exists but route not found
            // 2. Menu fetch completed but empty
            //
            isUnauthorized:
                (menuItems.length > 0 && !match) ||
                (menuFetched && menuItems.length === 0),

            // -----------------------------------------------------
            // FULL ACCESS CONDITION
            // -----------------------------------------------------
            // If route exists but permission key missing,
            // allow unrestricted access.
            //
            shouldGrantFullAccess:
                !!match && !hasPermissionKey,
        }

    }, [
        pathname,
        search,
        indexedMenu,
        menuItems.length,
        menuFetched,
        allowedNonMenuRoutes,
    ])

    // -------------------------------------------------------------
    // CAN MANAGE DERIVATION
    // -------------------------------------------------------------
    // Converts permission string into boolean access.
    //
    // Example:
    // manage -> true
    // view   -> false
    //
    const canManage = useMemo(() => {

        // If route exists but permission config missing,
        // allow full access.
        if (shouldGrantFullAccess) return true

        if (!currentPermission) return false

        return (
            defaultPermissionConfig?.[
                currentPermission.toLowerCase()
            ]?.canManage ?? false
        )

    }, [
        currentPermission,
        defaultPermissionConfig,
        shouldGrantFullAccess,
    ])

    // -------------------------------------------------------------
    // NAVIGATE BACK
    // -------------------------------------------------------------
    // Helper for returning to previous page.
    //
    const goBack = () => navigate(-1)

    // -------------------------------------------------------------
    // ALL MENU LINKS
    // -------------------------------------------------------------
    // Flattened list used for:
    // - debugging
    // - search
    // - route helpers
    //
    const allMenuLinks = useMemo(() => {

        return flattenMenu(menuItems)
            .map((item) => ({
                link: item.link,
                title: item.title,
            }))
            .filter(Boolean)

    }, [menuItems])

    // -------------------------------------------------------------
    // PROVIDER
    // -------------------------------------------------------------
    return (
        <RoutePermissionContext.Provider
            value={{
                currentPermission,
                canManage,
                isUnauthorized,
                menuItems,
                refreshMenuItems,
                goBack,
                allMenuLinks,
                currentRole,
            }}
        >
            {children}
        </RoutePermissionContext.Provider>
    )
}

// -------------------------------------------------------------
// CUSTOM HOOK
// -------------------------------------------------------------
// Provides easy access to RoutePermissionContext.
//
export const useRoutePermission = () => {

    const context = useContext(RoutePermissionContext)

    if (!context) {
        throw new Error(
            "useRoutePermission must be used within RoutePermissionProvider"
        )
    }

    return context
}